/* eslint-disable @typescript-eslint/no-explicit-any */
import type z from 'zod'
import type { NitroFetchOptions } from 'nitropack'
import type { FormError } from '#ui/types'

import {
  validateSingleResponse,
  validateCollectionResponse,
  validatePaginatedResponse,
  validateLookupResponse,
  safeValidateResponse,
  isApiError,
  isValidationError,
  isUnauthorizedError,
  isForbiddenError,
  isNotFoundError,
  isRateLimitError,
  isServerError,
  getErrorMessage,
} from '~/schemas/api'
import type {
  ApiPaginatedResponse,
  BaseApiError,
  ApiLookupResponse,
} from '~/schemas/api'

export const useApiResponse = () => {
  const { showError } = useAlert()
  const router = useRouter()
  const isClient = import.meta.client

  function mapLaravelErrorsIntoFormErrors(
    errors: Record<string, string[]>
  ): FormError[] {
    return Object.entries(errors).map(([key, messages]) => ({
      name: key,
      message: messages[0] ?? '',
    }))
  }

  const handleApiResponse = async <T>(
    apiCall: Promise<unknown>,
    schema: z.ZodType<T>,
    type: 'single' | 'collection' | 'paginated' | 'lookup' = 'single'
  ) => {
    try {
      const response = await apiCall

      // Check for different error types
      if (isUnauthorizedError(response)) {
        // Redirect to login or refresh token
        await router.push('/login')
        throw new Error(`Unauthorized: ${getErrorMessage(response)}`)
      }

      if (isForbiddenError(response)) {
        if (isClient) {
          await showError('Nemate dozvolu', getErrorMessage(response))
        }
        throw new Error(`Forbidden: ${getErrorMessage(response)}`)
      }

      if (isNotFoundError(response)) {
        if (isClient) {
          await showError('Nije pronađeno', getErrorMessage(response))
        }
        throw new Error(`Not Found: ${getErrorMessage(response)}`)
      }

      if (isValidationError(response)) {
        throw {
          type: 'validation',
          message: response.message,
          errors: response.errors,
          status: response.status,
        }
      }

      if (isRateLimitError(response)) {
        const retryAfter = response.retry_after || 60
        if (isClient) {
          await showError(
            `Pokušajte ponovo za ${retryAfter} sekundi.`,
            'Previše zahteva'
          )
        }
        throw new Error(
          `Rate limit exceeded. Try again in ${retryAfter} seconds.`
        )
      }

      if (isServerError(response)) {
        console.error('Server error:', response)
        if (isClient) {
          await showError(getErrorMessage(response), 'Greška na serveru')
        }
        throw new Error(`Server error: ${getErrorMessage(response)}`)
      }

      if (isApiError(response)) {
        console.error('API error:', response)
        if (isClient) {
          await showError(getErrorMessage(response))
        }
        throw new Error(getErrorMessage(response))
      }

      switch (type) {
        case 'single':
          return validateSingleResponse(schema, response)
        case 'collection':
          return validateCollectionResponse(schema, response)
        case 'paginated':
          return validatePaginatedResponse(schema, response)
        case 'lookup':
          return validateLookupResponse(schema, response)
        default:
          throw new Error(`Unknown response type: ${type}`)
      }
    } catch (fetchError: any) {
      if (fetchError.status === 422) {
        const responseData = fetchError.data || {}

        throw {
          type: 'validation',
          message: responseData.message || 'Validation failed',
          errors: responseData.errors || {},
          status: 422,
          formErrors: mapLaravelErrorsIntoFormErrors(responseData.errors || {}),
        }
      }

      if (
        typeof fetchError === 'object' &&
        fetchError !== null &&
        'type' in fetchError
      ) {
        throw fetchError
      }

      console.error('Unhandled API error:', fetchError)
      throw fetchError
    }
  }

  const handleApiError = (error: unknown) => {
    // 1. Check for already structured validation errors
    if (typeof error === 'object' && error !== null && 'type' in error) {
      const structuredError = error as any

      if (structuredError.type === 'validation') {
        return {
          type: 'validation' as const,
          message: structuredError.message,
          errors: structuredError.errors,
          formErrors: mapLaravelErrorsIntoFormErrors(
            structuredError.errors || {}
          ),
        }
      }
    }

    const isFetchError = error instanceof Error && 'response' in error
    if (isFetchError) {
      const fetchError = error as any

      if (fetchError.response?.status === 422) {
        const laravelErrors = fetchError.response?._data?.errors || {}
        const message =
          fetchError.response?._data?.message || 'Validation failed'

        return {
          type: 'validation' as const,
          message,
          errors: laravelErrors,
          formErrors: mapLaravelErrorsIntoFormErrors(laravelErrors),
        }
      }

      // Other HTTP errors
      return {
        type: 'error' as const,
        message:
          fetchError.response?._data?.message ||
          fetchError.message ||
          'Server error',
        errors: null,
        formErrors: [],
      }
    }

    // 3. Generic error handling
    const message =
      error instanceof Error ? error.message : 'Neočekivana greška'

    return {
      type: 'error' as const,
      message,
      errors: null,
      formErrors: [],
    }
  }

  // Wrapper for fetch operations with automatic error handling
  const safeFetch = async <T>(
    url: string,
    schema: z.ZodType<T>,
    options: RequestInit = {},
    responseType: 'single' | 'collection' | 'paginated' = 'single'
  ) => {
    // Cast options to the correct type for $fetch to satisfy type requirements
    const apiCall = $fetch(url, options as NitroFetchOptions<string>)
    return handleApiResponse(apiCall, schema, responseType)
  }

  // Wrapper for Sanctum client with automatic error handling
  const safeSanctumFetch = async <T>(
    url: string,
    schema: z.ZodType<T>,
    options: any = {},
    responseType: 'single' | 'collection' | 'paginated' | 'lookup' = 'single'
  ) => {
    const sanctumClient = useSanctumClient()
    const apiCall = sanctumClient(url, options)
    return handleApiResponse(apiCall, schema, responseType)
  }

  // Batch API calls with error handling
  const handleBatchApiCalls = async <T extends Record<string, any>>(
    calls: T
  ): Promise<{ [K in keyof T]: Awaited<T[K]> }> => {
    const results = {} as { [K in keyof T]: Awaited<T[K]> }
    const errors: string[] = []

    for (const [key, promise] of Object.entries(calls)) {
      try {
        results[key as keyof T] = await promise
      } catch (error) {
        const errorInfo = handleApiError(error)
        errors.push(`${key}: ${errorInfo.message}`)
        console.error(`Batch call ${key} failed:`, error)
      }
    }

    if (errors.length > 0) {
      await showError('Delovi zahteva nisu uspeli', errors.join(', '))
    }

    return results
  }

  // Retry mechanism for failed API calls
  const retryApiCall = async <T>(
    apiCall: () => Promise<T>,
    maxRetries = 3,
    delay = 1000
  ): Promise<T> => {
    let lastError: unknown

    for (let i = 0; i < maxRetries; i++) {
      try {
        return await apiCall()
      } catch (error) {
        lastError = error

        // Don't retry on validation errors or client errors
        if (typeof error === 'object' && error !== null && 'type' in error) {
          if ((error as any).type === 'validation') {
            throw error
          }
        }

        // Don't retry on 4xx errors except 429 (rate limit)
        if (isApiError(error as any)) {
          const apiError = error as BaseApiError
          if (
            apiError.status >= 400 &&
            apiError.status < 500 &&
            apiError.status !== 429
          ) {
            throw error
          }
        }

        // Wait before retry
        if (i < maxRetries - 1) {
          await new Promise((resolve) =>
            setTimeout(resolve, delay * Math.pow(2, i))
          )
        }
      }
    }

    throw lastError
  }

  // Tipizovani helperi
  const fetchSingle = async <T>(
    url: string,
    schema: z.ZodType<T>,
    options: any = {}
  ): Promise<T> => {
    const sanctumClient = useSanctumClient()
    const apiCall = sanctumClient(url, options)
    const result = await handleApiResponse(apiCall, schema, 'single')
    if (
      result &&
      typeof result === 'object' &&
      !Array.isArray(result) &&
      !('meta' in result)
    ) {
      return result as T
    }
    throw new Error('Response is not a single item')
  }

  const fetchCollection = async <T>(
    url: string,
    schema: z.ZodType<T>,
    options: any = {}
  ): Promise<T[]> => {
    const sanctumClient = useSanctumClient()
    const apiCall = sanctumClient(url, options)
    const result = await handleApiResponse(apiCall, schema, 'collection')
    if (Array.isArray(result)) {
      return result
    }
    throw new Error('Response is not a collection')
  }

  const fetchPaginated = async <T>(
    url: string,
    schema: z.ZodType<T>,
    options: any = {}
  ): Promise<ApiPaginatedResponse<T>> => {
    const sanctumClient = useSanctumClient()
    const apiCall = sanctumClient(url, options)
    const result = await handleApiResponse(apiCall, schema, 'paginated')
    if (
      result &&
      typeof result === 'object' &&
      'data' in result &&
      'meta' in result
    ) {
      return result as ApiPaginatedResponse<T>
    }
    throw new Error('Response is not paginated')
  }

  const fetchLookup = async <T>(
    url: string,
    schema: z.ZodType<T>,
    options: any = {}
  ): Promise<ApiLookupResponse<T>> => {
    const sanctumClient = useSanctumClient()
    const apiCall = sanctumClient(url, options)
    const result = await handleApiResponse(apiCall, schema, 'lookup')
    if (
      result &&
      typeof result === 'object' &&
      'list' in result &&
      'selected' in result
    ) {
      return result as ApiLookupResponse<T>
    }
    throw new Error('Response is not a lookup')
  }

  return {
    // Main functions
    handleApiResponse,
    handleApiError,

    // Convenience wrappers
    safeFetch,
    safeSanctumFetch,

    // Batch and retry
    handleBatchApiCalls,
    retryApiCall,

    // Direct validation functions
    validateSingleResponse,
    validateCollectionResponse,
    validatePaginatedResponse,
    validateLookupResponse,
    safeValidateResponse,

    // Error type checkers
    isApiError,
    isValidationError,
    isUnauthorizedError,
    isForbiddenError,
    isNotFoundError,
    isRateLimitError,
    isServerError,
    getErrorMessage,

    // Typed fetch helpers
    fetchSingle,
    fetchCollection,
    fetchPaginated,
    fetchLookup,
  }
}
